import { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { cancelOrder, editBracketOrder } from 'actions/trade';
import { ORDER_CATEGORY } from 'constants/enums';
import { addLogsToSentry } from 'helpers/sentry';
import { isMobileOrTablet } from 'helpers/utils';
import { userSelector } from 'selectors/otherSelectors';
import { selectedProductOpenOrdersSelector } from 'selectors/tradeSelectors';

const OpenOrderLine = ({
  order,
  widgetRef,
  isNewTab,
  isChartReady,
  currentActiveTab,
}) => {
  const orderLine = useRef(null);
  // const markPrice = markPriceSelectorById(order.product.id.toString());
  const [showOrderLine, setShowOrderLine] = useState(false);
  const previousTab = useRef('');

  const user = useSelector(userSelector);
  const dispatch = useDispatch();
  const color = order.side === 'sell' ? '#e55541' : '#28d591';
  const buttonCloseColor = '#153034';
  const buttonBorderColor = order.side === 'sell' ? '#e55541' : '#00ce7d';
  const orderInfo = JSON.stringify(order);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // eslint-disable-next-line no-underscore-dangle
      if (isChartReady && widgetRef?.current?._id && showOrderLine) {
        try {
          orderLine.current = widgetRef.current
            .activeChart()
            .createOrderLine()
            .onCancel(function () {
              this.setText('Cancelling...');
              dispatch(
                cancelOrder(
                  {
                    id: order.id,
                    product_id: order.product.id,
                  },
                  {
                    chart: true,
                  },
                  { orderCategory: ORDER_CATEGORY.OPEN_ORDER }
                )
              );
            })
            .onMove(function () {
              this.setText('Updating...');

              dispatch(
                editBracketOrder(
                  {
                    id: order.id,
                    product_id: order.product.id,
                    limit_price: this.getPrice().toString(),
                  },
                  {
                    chart: true,
                  }
                )
              ).catch(() => {
                if (orderLine.current) {
                  orderLine.current.setPrice(order.stop_price);
                  orderLine.current.setText(`${order.side} @ ${order.limit_price}`);
                }
              });
            })
            .setModifyTooltip('Quantity')
            .setText(`${order.side} @ ${order.limit_price}`)
            .setLineColor(color)
            .setBodyBorderColor(buttonBorderColor)
            .setBodyBackgroundColor('#153034')
            .setQuantityBackgroundColor(color)
            .setQuantityBorderColor(color)
            .setBodyTextColor('#ffffff')
            .setBodyFont('12px Verdana')
            .setQuantityFont('12px Verdana')
            .setCancelButtonBorderColor(buttonBorderColor)
            .setCancelButtonBackgroundColor(buttonCloseColor)
            .setCancelButtonIconColor('#e55541')
            .setPrice(order.limit_price)
            .setQuantity(order.size)
            .setExtendLeft(true)
            .setLineLength(isNewTab ? 68 : 65);

          if (isMobileOrTablet()) {
            orderLine.current.setBodyFont('10px Verdana').setQuantityFont('10px Verdana');
          }
        } catch (e) {
          console.error('DEBUG: Error creating order line', e);
          addLogsToSentry(user, `Error creating order line ${e}`);
          // console.log(' open Orders error', e);
        }
      }
    }, 1000);
    return function cleanUp() {
      clearTimeout(timeoutId);
      if (orderLine.current) {
        if (orderLine.current._line) {
          orderLine.current.remove();
        }
        orderLine.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderInfo, widgetRef.current, isChartReady, currentActiveTab, showOrderLine]);

  useEffect(() => {
    if (previousTab?.current !== currentActiveTab) {
      setShowOrderLine(false);
      setTimeout(() => {
        setShowOrderLine(true);
      }, 1000);
    }
  }, [currentActiveTab]);

  return null;
};

// @todo bala move it formula.js and add unit test for it
// const checkImmediateExecution = (side, value, markPrice) => {
//   ///const selectedProductIdSelector)
//   // for buy orders: if new limit price > mark price: show immediate liquidation message
//   // for sell orders: if new limit price < mark price: show immediate liquidation message
//   if (R.equals(side, 'sell')) {
//     return R.lte(Number(value), Number(markPrice));
//   } else {
//     return R.gte(Number(value), Number(markPrice));
//   }
// };

const OpenOrdersWidget = ({ isNewTab, widgetRef, isChartReady, currentActiveTab }) => {
  const orders = useSelector(selectedProductOpenOrdersSelector);
  return orders.map(order => (
    <OpenOrderLine
      key={order.id}
      order={order}
      isNewTab={isNewTab}
      widgetRef={widgetRef}
      isChartReady={isChartReady}
      currentActiveTab={currentActiveTab}
    />
  ));
};

export default OpenOrdersWidget;
